import configJson from 'config.json';
import { ReadonlyObjectDeep } from 'type-fest/source/readonly-deep';
import { IncomingMessage } from 'http';
import { X_ZOOCASA_FEATURES_HEADER_NAME } from 'constants/headers';

import {
  EXP_COMMERCIAL_TENANT_ID,
  EXP_IN_SERVICE_TENANT_ID,
  EXP_REALTY_CA_TENANT_ID,
  EXP_REALTY_US_TENANT_ID,
  ZOOCASA_TENANT_ID,
  type TenantId,
  type ThemeConfig,
  type ThemeNames,
} from './types';

import {
  type ExpInServiceTenant,
  type ExpRealtyCaTenant,
  type ExpRealtyUSTenant,
  type ExpCommercialTenant,
  type ZoocasaTenant,
  expInServiceTenant,
  expRealtyUSTenant,
  expRealtyCATenant,
  expCommercialTenant,
  zoocasaTenant,
} from './definitions';

import type { FeaturesType } from 'contexts';

export type TenantTypes = ReturnType<typeof getTenantById>;

/**
 * Retrieves a tenant configuration by its ID.
 *
 * This function returns the tenant configuration for a given tenant ID. The tenant ID must be one of
 * the valid tenant identifiers defined in the system:
 * - {@link ZOOCASA_TENANT_ID} - Zoocasa tenant for Canadian market
 * - {@link EXP_REALTY_CA_TENANT_ID} - eXp Realty Canada tenant
 * - {@link EXP_REALTY_US_TENANT_ID} - eXp Realty US tenant
 * - {@link EXP_COMMERCIAL_TENANT_ID} - eXp Commercial tenant
 * - {@link EXP_IN_SERVICE_TENANT_ID} - eXp In-Service tenant
 *
 * Each tenant configuration may include:
 * - Theme configuration
 * - Localization settings (languages, countries, i18n namespaces)
 * - Header and footer configurations
 * - Agent search settings
 * - Debug options
 * - Features
 * - Routes
 *
 * @example
 * ```typescript
 * // Get Zoocasa tenant configuration
 * const zoocasaTenant = getTenantById(ZOOCASA_TENANT_ID);
 *
 * // Get eXp Realty Canada tenant configuration
 * const expRealtyCATenant = getTenantById(EXP_REALTY_CA_TENANT_ID);
 * ```
 *
 * @param id The ID of the tenant to retrieve. Must be a valid {@link TenantId}.
 * @returns The tenant configuration object of type {@link Tenant}.
 */
export function getTenantById(id: TenantId): ExpInServiceTenant | ExpRealtyUSTenant | ExpRealtyCaTenant | ExpCommercialTenant | ZoocasaTenant | undefined {
  switch (id) {
    case EXP_IN_SERVICE_TENANT_ID:
      return expInServiceTenant;
    case ZOOCASA_TENANT_ID:
      return zoocasaTenant;
    case EXP_REALTY_CA_TENANT_ID:
      return expRealtyCATenant;
    case EXP_REALTY_US_TENANT_ID:
      return expRealtyUSTenant;
    case EXP_COMMERCIAL_TENANT_ID:
      return expCommercialTenant;
    default:
      return undefined;
  }
}

/**
 * Retrieves a tenant configuration by its debug header value for testing and QA purposes.
 *
 * This function allows impersonating different tenants during development and testing by returning a tenant configuration
 * based on a specific debug header value. Each tenant defines its own debug header value in their
 * `debug.forceTenantHeaderValue` property and a client can use this function to retrieve the
 * tenant configuration by passing the debug header value to this function.
 *
 * This is particularly useful for:
 * - Testing tenant-specific features
 * - QA verification of tenant configurations
 * - Debugging tenant-related issues
 * - Local development of tenant-specific functionality
 *
 * @example
 * ```typescript
 * // Example header value in HTTP request
 * // X-Zoocasa-Features: useTenant="expInService"
 * const tenant = getTenantByDebugHeaderValue('useTenant=\"expInService\"'); // Returns eXp In-Service tenant
 * ```
 *
 * @param debugHeaderValue The debug header value specified in the tenant's debug configuration
 * @returns The matching tenant configuration or undefined if no tenant matches the debug header value
 */
export function getTenantByDebugHeaderValue(debugHeaderValue: string = ''): TenantTypes {
  if (debugHeaderValue.includes(expInServiceTenant.debug.forceTenantHeaderValue)) {
    return expInServiceTenant;
  }
  if (debugHeaderValue.includes(expRealtyUSTenant.debug.forceTenantHeaderValue)) {
    return expRealtyUSTenant;
  }
  if (debugHeaderValue.includes(expRealtyCATenant.debug.forceTenantHeaderValue)) {
    return expRealtyCATenant;
  }
  if (debugHeaderValue.includes(expCommercialTenant.debug.forceTenantHeaderValue)) {
    return expCommercialTenant;
  }
  if (debugHeaderValue.includes(zoocasaTenant.debug.forceTenantHeaderValue)) {
    return zoocasaTenant;
  }
  return undefined;
}

/**
 * Retrieves the tenant ID for a given host.
 *
 * This function returns the tenant ID for a given host. The host must be one of the valid hosts defined in the system:
 * - {@link expInServiceTenant.hosts} - eXp In-Service tenant
 * - {@link expRealtyUSTenant.hosts} - eXp Realty US tenant
 * - {@link expRealtyCATenant.hosts} - eXp Realty CA tenant
 * - {@link expCommercialTenant.hosts} - eXp Commercial tenant
 * - {@link zoocasaTenant.hosts} - Zoocasa tenant
 *
 * @param host The host to retrieve the tenant ID for. Must be a valid host.
 * @returns The tenant ID or undefined if no tenant matches the host
 */
export function getTenantIdByHost(host: string = '', defaultTenantId = configJson.defaultTenantId as TenantId): TenantId | undefined {
  if (expInServiceTenant.hosts.includes(host)) {
    return EXP_IN_SERVICE_TENANT_ID;
  }
  if (expRealtyUSTenant.hosts.includes(host)) {
    return EXP_REALTY_US_TENANT_ID;
  }
  if (expRealtyCATenant.hosts.includes(host)) {
    return EXP_REALTY_CA_TENANT_ID;
  }
  if (expCommercialTenant.hosts.includes(host)) {
    return EXP_COMMERCIAL_TENANT_ID;
  }
  if (zoocasaTenant.hosts.includes(host)) {
    return ZOOCASA_TENANT_ID;
  }
  return defaultTenantId;
}

/**
 * Retrieves the tenant ID for a given string.
 *
 * This function returns the tenant ID for a given string. The string must be one of the valid tenant identifiers defined in the system:
 * - 'expInService' - eXp In-Service tenant
 * - 'expRealtyUS' - eXp Realty US tenant
 * - 'expRealtyCA' - eXp Realty CA tenant
 * - 'expCommercial' - eXp Commercial tenant
 * - 'zoocasa' - Zoocasa tenant
 *
 * @param tenantId The string to retrieve the tenant ID for. Must be a valid tenant identifier.
 * @returns The tenant ID or {@link configJson.defaultTenantId} if no tenant matches the string
 */
export function getTenantIdFromString(tenantId: string): TenantId | undefined {
  switch (tenantId.toLowerCase()) {
    case 'expInService'.toLowerCase():
      return EXP_IN_SERVICE_TENANT_ID;
    case 'expRealtyUS'.toLowerCase():
      return EXP_REALTY_US_TENANT_ID;
    case 'expRealtyCA'.toLowerCase():
      return EXP_REALTY_CA_TENANT_ID;
    case 'expCommercial'.toLowerCase():
      return EXP_COMMERCIAL_TENANT_ID;
    case 'zoocasa'.toLowerCase():
      return ZOOCASA_TENANT_ID;
    default:
      return configJson.defaultTenantId as TenantId;
  }
}

/**
 * Retrieves the tenant configuration for a given host.
 *
 * This function returns the tenant configuration for a given host. The host must be one of the valid hosts defined in the system:
 * - {@link expInServiceTenant.hosts} - eXp In-Service tenant
 * - {@link expRealtyUSTenant.hosts} - eXp Realty US tenant
 * - {@link expRealtyCATenant.hosts} - eXp Realty CA tenant
 * - {@link expCommercialTenant.hosts} - eXp Commercial tenant
 * - {@link zoocasaTenant.hosts} - Zoocasa tenant
 *
 * @param host The host to retrieve the tenant configuration for. Must be a valid host.
 * @returns The tenant configuration or undefined if no tenant matches the host
 */
export function getTenantByHost(host: string = '', defaultTenantId = configJson.defaultTenantId as TenantId): TenantTypes | undefined {
  const tenantId = getTenantIdByHost(host) || defaultTenantId;
  if (!tenantId) {
    return undefined;
  }
  return getTenantById(tenantId);
}

/**
 * Retrieves the theme configuration for a given tenant ID.
 *
 * This function returns the theme configuration for a given tenant ID. The tenant ID must be one of
 * the valid tenant identifiers defined in the system:
 * - {@link ZOOCASA_TENANT_ID} - Zoocasa tenant
 * - {@link EXP_REALTY_CA_TENANT_ID} - eXp Realty CA tenant
 * - {@link EXP_REALTY_US_TENANT_ID} - eXp Realty US tenant
 * - {@link EXP_COMMERCIAL_TENANT_ID} - eXp Commercial tenant
 * - {@link EXP_IN_SERVICE_TENANT_ID} - eXp In-Service tenant
 * @param tenantId The ID of the tenant to retrieve the theme configuration for. Must be a valid {@link TenantId}.
 * @returns The theme configuration object of type {@link ThemeConfig}.
 */
export function getThemeByTenantId(tenantId: TenantId): ReadonlyObjectDeep<ThemeConfig<ThemeNames>> {
  return getTenantById(tenantId).theme;
}

/**
 * Retrieves the tenant configuration for a given request.
 *
 * This function determines the appropriate tenant configuration based on either:
 * 1. A debug header value (X-Zoocasa-Features) that forces a specific tenant
 * 2. The request's host header that maps to a tenant's domain
 *
 * The function first checks for a debug header that forces a specific tenant (useful for testing).
 * If no debug header is present or doesn't match any tenant, it falls back to determining
 * the tenant based on the request's host header.
 *
 * @example
 * ```typescript
 * // Get tenant from a request with debug header
 * const tenant = getTenantFromRequest({
 *   headers: {
 *     'x-zoocasa-features': 'useTenant="expInService"',
 *     host: 'zoocasa.com'
 *   }
 * }); // Returns eXp In-Service tenant
 *
 * // Get tenant from a request with host header only
 * const tenant = getTenantFromRequest({
 *   headers: {
 *     host: 'zoocasa.com'
 *   }
 * }); // Returns Zoocasa tenant
 * ```
 *
 * @param req - The request object containing headers. Must include either:
 *              - X-Zoocasa-Features header for debug/force tenant
 *              - host header for normal tenant resolution
 * @param defaultTenantId Optional. The tenant ID to use if no tenant can be determined from the request headers
 * @returns The matching tenant configuration or undefined if no tenant can be determined.
 * @see {@link getTenantByDebugHeaderValue} for debug header tenant resolution
 * @see {@link getTenantIdByHost} for host-based tenant resolution
 */
export function getTenantFromRequestHeader(req?: Pick<IncomingMessage, 'headers'>, defaultTenantId = configJson.defaultTenantId as TenantId): TenantTypes {
  let tenant: TenantTypes;
  if (!req) {
    if (defaultTenantId) {
      return getTenantById(defaultTenantId);
    }
    return undefined;
  }

  tenant = getTenantByDebugHeaderValue(req.headers[X_ZOOCASA_FEATURES_HEADER_NAME] as string);

  if (!tenant) {
    const tenantId = getTenantIdByHost(req.headers.host);

    if (tenantId) {
      tenant = getTenantById(tenantId);
    }
  }

  if (!tenant && defaultTenantId) {
    tenant = getTenantById(defaultTenantId);
  }

  return tenant;
}

/**
 * Determines the impersonated tenant based on the provided features.
 *
 * If the {@link FeaturesType.useTenant} feature is provided, it checks
 * for specific tenant feature flags by matching the value of the feature
 * to the debug value of the tenant {@link Tenant.debug forceTenantFeatureValue}.
 *
 * Note - this is a case-insensitive match to make the override flexible.
 *
 * @param features The features object that may contain tenant information.
 * @returns The matching tenant configuration or undefined if no tenant override was set.
 */
export function getTenantFromFeatureFlag(features?: FeaturesType): TenantTypes {
  if ((!features) || (typeof features.useTenant !== 'string')) {
    return undefined;
  }
  const featureOverride = features?.useTenant?.toLowerCase() ?? '';
  switch (featureOverride) {
    case getTenantById(EXP_REALTY_CA_TENANT_ID).debug.forceTenantFeatureValue.toLowerCase():
      return getTenantById(EXP_REALTY_CA_TENANT_ID);
    case getTenantById(EXP_REALTY_US_TENANT_ID).debug.forceTenantFeatureValue.toLowerCase():
      return getTenantById(EXP_REALTY_US_TENANT_ID);
    case getTenantById(EXP_IN_SERVICE_TENANT_ID).debug.forceTenantFeatureValue.toLowerCase():
      return getTenantById(EXP_IN_SERVICE_TENANT_ID);
    case getTenantById(EXP_COMMERCIAL_TENANT_ID).debug.forceTenantFeatureValue.toLowerCase():
      return getTenantById(EXP_COMMERCIAL_TENANT_ID);
    default:
      return undefined;
  }
}